/**
 * Hook per caricare lazy le foto profilo da WAHA
 * Usa IntersectionObserver per caricare solo quando visibile
 * Con concurrency limiter per evitare 504 timeout
 */

import { useState, useEffect, useRef } from 'react';
import { getWahaService } from '../../services/wahaApiService';
import { useWhatsAppChatStore } from '../../stores/whatsappChatStore';
import { debugLog } from '../../../components/whatsapp2/chat/ChatDebugPanel';

// Cache globale per evitare richieste duplicate
const profilePicCache = new Map<string, string | null>();
const pendingRequests = new Map<string, Promise<string | null>>();

// Concurrency limiter: max 3 richieste parallele
const MAX_CONCURRENT = 3;
let activeRequests = 0;
const queue: Array<() => void> = [];

function enqueue(): Promise<void> {
  if (activeRequests < MAX_CONCURRENT) {
    activeRequests++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    queue.push(() => {
      activeRequests++;
      resolve();
    });
  });
}

function dequeue() {
  activeRequests--;
  if (queue.length > 0) {
    const next = queue.shift()!;
    next();
  }
}

export function useProfilePicture(chatId: string | undefined) {
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const hasLoadedRef = useRef(false);
  const prevChatIdRef = useRef<string | undefined>(undefined);
  const { wahaConfig } = useWhatsAppChatStore();

  // Reset quando cambia chatId (es. react-window riusa il componente con un item diverso)
  if (chatId !== prevChatIdRef.current) {
    prevChatIdRef.current = chatId;
    hasLoadedRef.current = false;
    // Se la nuova chat è già in cache, settala subito per evitare flash
    if (chatId && profilePicCache.has(chatId)) {
      const cached = profilePicCache.get(chatId) || null;
      if (cached !== profilePicUrl) {
        setProfilePicUrl(cached);
      }
      hasLoadedRef.current = true;
    } else if (profilePicUrl !== null) {
      setProfilePicUrl(null);
    }
  }

  useEffect(() => {
    if (!chatId || !wahaConfig || hasLoadedRef.current) return;

    // Check cache first
    if (profilePicCache.has(chatId)) {
      setProfilePicUrl(profilePicCache.get(chatId) || null);
      hasLoadedRef.current = true;
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    let cancelled = false;

    const observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && !hasLoadedRef.current) {
          hasLoadedRef.current = true;
          observer.disconnect();

          // Check if already loading
          if (pendingRequests.has(chatId)) {
            const result = await pendingRequests.get(chatId)!.catch(() => null);
            if (!cancelled) setProfilePicUrl(result || null);
            return;
          }

          if (!cancelled) setIsLoading(true);

          // Aspetta il turno nel concurrency limiter
          await enqueue();

          try {
            const service = getWahaService();
            const loadPromise = service.getProfilePicture(chatId);
            pendingRequests.set(chatId, loadPromise);

            const url = await loadPromise;
            profilePicCache.set(chatId, url);
            if (!cancelled) setProfilePicUrl(url);
            debugLog('ProfilePic', `${url ? 'OK' : 'nessuna foto'} per ${chatId.slice(0, 25)}`);
          } catch (err) {
            // Niente cache sugli errori (WAHA lento, sessione in riavvio): la foto si richiede
            // alla prossima comparsa della riga, invece di restare sulle iniziali fino al ricaricamento.
            if (!cancelled) setProfilePicUrl(null);
            debugLog('ProfilePic', `ERRORE per ${chatId.slice(0, 25)}: ${(err as Error)?.message || err}`, null, 'warn');
          } finally {
            pendingRequests.delete(chatId);
            dequeue();
            if (!cancelled) setIsLoading(false);
          }
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(element);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [chatId, wahaConfig]);

  return { profilePicUrl, isLoading, ref: elementRef };
}

/**
 * Pulisce la cache delle foto profilo
 */
export function clearProfilePicCache() {
  profilePicCache.clear();
}
